'use client';

import {
  CalendarIcon,
  ChevronDown as ChevronDownIcon,
  Filter as FilterIcon,
  ListFilter as ListFilterIcon,
  Tag as TagIcon,
  XCircleIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  candidateStatuses,
  candidateTags,
  createDefaultCandidateFilters,
  type CandidateFilters,
  type CandidateStatusId,
  type CandidateTagId,
} from '@/types/candidate';
import DateRangeFilter from './date-range-filter';

type FilterMenuProps = {
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
  onFiltersReset?: () => void;
  className?: string;
};

const formatDisplayDate = (date: Date | null) => {
  if (!date) {
    return '';
  }
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getActiveFilterCount = (filters: CandidateFilters) => {
  const { tags, status, dateRange } = filters;
  const dateRangeIsActive = Boolean(dateRange.from || dateRange.to);

  return tags.length + (status ? 1 : 0) + (dateRangeIsActive ? 1 : 0);
};

const formatFilterCount = (count: number) =>
  `${count} filter${count === 1 ? '' : 's'}`;

const FilterMenu = ({
  filters,
  onFiltersChange,
  onFiltersReset,
  className,
}: FilterMenuProps) => {
  const activeFilterCount = useMemo(
    () => getActiveFilterCount(filters),
    [filters]
  );
  const hasActiveFilters = activeFilterCount > 0;

  const [expandedSections, setExpandedSections] = useState<
    Record<'tags' | 'status' | 'dates', boolean>
  >({
    tags: false,
    status: false,
    dates: false,
  });

  const toggleSection = (section: 'tags' | 'status' | 'dates') => {
    setExpandedSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  };

  const tagsActive = filters.tags.length > 0;
  const statusActive = Boolean(filters.status);
  const datesActive = Boolean(filters.dateRange.from || filters.dateRange.to);

  const dateSummary = useMemo(() => {
    const { from, to } = filters.dateRange;

    if (!from && !to) {
      return 'Alle data';
    }

    if (from && to) {
      return `${formatDisplayDate(from)} – ${formatDisplayDate(to)}`;
    }

    if (from) {
      return `Vanaf ${formatDisplayDate(from)}`;
    }

    return `Tot ${formatDisplayDate(to)}`;
  }, [filters.dateRange]);

  const selectedStatusLabel = useMemo(() => {
    if (!filters.status) {
      return 'Alle statussen';
    }
    return (
      candidateStatuses.find((status) => status.id === filters.status)?.label ??
      'Onbekend'
    );
  }, [filters.status]);

  const handleTagsChange = (tagId: CandidateTagId, isChecked: boolean) => {
    const nextTags = isChecked
      ? [...new Set([...filters.tags, tagId])]
      : filters.tags.filter((id) => id !== tagId);
    onFiltersChange({ ...filters, tags: nextTags });
    setExpandedSections((previous) => ({ ...previous, tags: true }));
  };

  const handleStatusChange = (statusId: CandidateStatusId | null) => {
    onFiltersChange({ ...filters, status: statusId });
    setExpandedSections((previous) => ({ ...previous, status: true }));
  };

  const handleDateRangeChange = (nextRange: CandidateFilters['dateRange']) => {
    onFiltersChange({ ...filters, dateRange: nextRange });
    setExpandedSections((previous) => ({ ...previous, dates: true }));
  };

  const handleResetFilters = () => {
    if (onFiltersReset) {
      onFiltersReset();
      setExpandedSections({ tags: false, status: false, dates: false });
      return;
    }

    onFiltersChange(createDefaultCandidateFilters());
    setExpandedSections({ tags: false, status: false, dates: false });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant='outline'
          size="lg"
          className={cn(
            'flex w-full items-center justify-between gap-2 sm:w-auto border-none bg-primary-bg inset-shadow-sm',
            className
          )}
        >
          <span className="flex items-center gap-2">
            <FilterIcon className="h-4 w-4" />
            Filters
          </span>
          {hasActiveFilters ? (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {activeFilterCount}
            </span>
          )
            : null}
          <ChevronDownIcon className="h-4 w-4 text-slate-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={8}
        className="w-72 p-2 bg-white border-slate-200 shadow-xl"
        sideOffset={8}
      >
        <div className="space-y-2">
          <div className="rounded-md border border-slate-200">
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm font-medium',
                expandedSections.tags ? 'bg-muted' : 'hover:bg-muted/60',
                tagsActive && !expandedSections.tags ? 'text-primary' : null
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleSection('tags');
              }}
            >
              <span className="flex items-center gap-2">
                <TagIcon className="h-4 w-4 text-slate-500" />
                Tags
              </span>
              <span className="ml-auto flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs text-slate-500 text-right',
                    tagsActive && 'rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary'
                  )}
                >
                  {tagsActive ? filters.tags.length : 'Alle'}
                </span>
                <ChevronDownIcon
                  className={cn(
                    'h-4 w-4 text-slate-500 transition-transform',
                    expandedSections.tags ? 'rotate-180' : 'rotate-0'
                  )}
                />
              </span>
            </button>
            {expandedSections.tags ? (
              <div className="space-y-1 px-1 pb-3">
                {candidateTags.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag.id}
                    checked={filters.tags.includes(tag.id)}
                    onCheckedChange={(checked) => handleTagsChange(tag.id, checked === true)}
                    className="text-sm"
                  >
                    {tag.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-md border border-slate-200">
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm font-medium',
                expandedSections.status ? 'bg-muted' : 'hover:bg-muted/60',
                statusActive && !expandedSections.status ? 'text-primary' : null
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleSection('status');
              }}
            >
              <span className="flex items-center gap-2">
                <ListFilterIcon className="h-4 w-4 text-slate-500" />
                Status
              </span>
              <span className="ml-auto flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'max-w-[150px] text-xs text-slate-500 text-right truncate',
                    statusActive && 'rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary'
                  )}
                  title={selectedStatusLabel}
                >
                  {selectedStatusLabel}
                </span>
                <ChevronDownIcon
                  className={cn(
                    'h-4 w-4 text-slate-500 transition-transform',
                    expandedSections.status ? 'rotate-180' : 'rotate-0'
                  )}
                />
              </span>
            </button>
            {expandedSections.status ? (
              <div className="px-1 pb-3">
                <DropdownMenuRadioGroup
                  value={filters.status ?? 'all'}
                  onValueChange={(value) =>
                    handleStatusChange(value === 'all' ? null : (value as CandidateStatusId))
                  }
                  className="space-y-1"
                >
                  <DropdownMenuRadioItem value="all" className="text-sm">
                    Alle statussen
                  </DropdownMenuRadioItem>
                  {candidateStatuses.map((status) => (
                    <DropdownMenuRadioItem key={status.id} value={status.id} className="text-sm">
                      {status.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </div>
            ) : null}
          </div>

          <div className="rounded-md border border-slate-200">
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm font-medium',
                expandedSections.dates ? 'bg-muted' : 'hover:bg-muted/60',
                datesActive && !expandedSections.dates ? 'text-primary' : null
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleSection('dates');
              }}
            >
              <span className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-slate-500" />
                Datum (inzending)
              </span>
              <span className="ml-auto flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'max-w-[150px] text-xs text-slate-500 text-right truncate',
                    datesActive && 'rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary'
                  )}
                  title={dateSummary}
                >
                  {dateSummary}
                </span>
                <ChevronDownIcon
                  className={cn(
                    'h-4 w-4 text-slate-500 transition-transform',
                    expandedSections.dates ? 'rotate-180' : 'rotate-0'
                  )}
                />
              </span>
            </button>
            {expandedSections.dates ? (
              <div>
                <DateRangeFilter
                  value={filters.dateRange}
                  onChange={handleDateRangeChange}
                />
              </div>
            ) : null}
          </div>
        </div>

        <DropdownMenuSeparator />
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 px-1 pb-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-0! text-sm text-slate-500 hover:text-red-600"
              onClick={handleResetFilters}
            >
              <XCircleIcon className="mr-2 h-4 w-4" />
              Reset filters
            </Button>
            <span className="text-xs text-slate-500">
              {formatFilterCount(activeFilterCount)}
            </span>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterMenu;
